import React, { useMemo } from 'react';
import clsx from 'clsx';
import { estadisticasOt, folioTexto, diasAbierta } from '../../utils/ordenesTrabajo';

const Tile = ({ label, value, sub }) => (
  <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2.5 shadow-sm">
    <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="mt-0.5 text-xl font-semibold tabular-nums text-azul-surelia">{value}</div>
    {sub && <div className="text-xs text-zinc-400">{sub}</div>}
  </div>
);

const dias = (n) => (n === null || n === undefined ? '—' : `${n} d`);

function TablaFlujo({ flujo }) {
  const maxAbiertas = Math.max(1, ...flujo.fases.map((f) => f.abiertas));
  return (
    <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden">
      <div className="flex items-baseline justify-between px-3 py-2 border-b border-zinc-100">
        <span className="text-sm font-semibold text-zinc-800">{flujo.flujo.nombre}</span>
        <span className="text-xs text-zinc-500 tabular-nums">
          {flujo.abiertas} abiertas
          {flujo.objetivoTotal ? ` · objetivo ${flujo.objetivoTotal} d` : ''}
          {flujo.diasPromedioCierre !== null ? ` · real ${flujo.diasPromedioCierre} d` : ''}
        </span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
            <th className="px-3 py-2">Fase</th>
            <th className="px-3 py-2 text-right">Abiertas</th>
            <th className="px-3 py-2 text-right">Fuera de tiempo</th>
            <th className="px-3 py-2 text-right">Objetivo</th>
            <th className="px-3 py-2 text-right">Real</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {flujo.fases.map((f) => {
            const excede = f.fase.diasObjetivo && f.promedioDias !== null && f.promedioDias > f.fase.diasObjetivo;
            return (
              <tr key={f.fase.id}>
                <td className="px-3 py-2">
                  <div className="text-zinc-800">{f.fase.nombre}</div>
                  <div className="mt-1 h-1 rounded bg-zinc-100">
                    <div className="h-1 rounded bg-cian-digital" style={{ width: `${(f.abiertas / maxAbiertas) * 100}%` }} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-zinc-800">{f.abiertas}</td>
                <td className={clsx('px-3 py-2 text-right tabular-nums', f.vencidas ? 'text-red-600 font-medium' : 'text-zinc-400')}>{f.vencidas || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{dias(f.fase.diasObjetivo)}</td>
                <td className={clsx('px-3 py-2 text-right tabular-nums', excede ? 'text-red-600 font-medium' : 'text-zinc-600')} title={`${f.muestras} pasos medidos`}>
                  {dias(f.promedioDias)}
                </td>
              </tr>
            );
          })}
          {flujo.fases.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-400">Sin fases configuradas</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Processing statistics over every OT of the org (open and closed), per flow.
 *
 * @param {Object} props
 * @param {Array<Object>} props.rows  - every decorated OT
 * @param {Object} props.config  - resolved config
 * @param {Function} props.onOpen  - (ot)
 */
export function OtEstadisticas({ rows, config, onOpen }) {
  const s = useMemo(() => estadisticasOt(rows, config), [rows, config]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Tile
          label="Abiertas"
          value={s.abiertas}
          sub={s.flujos.map((f) => `${f.abiertas} ${f.flujo.nombre.toLowerCase()}`).concat(s.porTipo.otros ? [`${s.porTipo.otros} de otro tipo`] : []).join(' · ')}
        />
        <Tile label="Abiertas 30 días" value={s.abiertas30} />
        <Tile label="Cerradas 30 días" value={s.cerradas30} />
        <Tile label="Entregadas 90 días" value={s.entregadas90} sub={s.cerradas90 ? `de ${s.cerradas90} cerradas` : null} />
        <Tile label="Días a cierre" value={dias(s.diasPromedioCierre)} sub="promedio, 90 días" />
        <Tile label="Cerradas" value={s.cerradas} sub="histórico" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {s.flujos.map((f) => <TablaFlujo key={f.flujo.id} flujo={f} />)}
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
              <th className="px-3 py-2">Más viejas abiertas</th>
              <th className="px-3 py-2">Fase</th>
              <th className="px-3 py-2 text-right">Días</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {s.masViejas.map((ot) => (
              <tr key={ot.id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => onOpen?.(ot)}>
                <td className="px-3 py-2">
                  <span className="text-xs font-semibold tabular-nums text-azul-surelia mr-2">{folioTexto(ot.folio)}</span>
                  <span className="text-zinc-800">{ot.titulo}</span>
                </td>
                <td className="px-3 py-2 text-zinc-600">{ot.faseNombre || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{diasAbierta(ot)}</td>
              </tr>
            ))}
            {s.masViejas.length === 0 && (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-zinc-400">Sin órdenes abiertas</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OtEstadisticas;
